import { useEffect, useRef } from "react";

/**
 * useVisibleAnimation — run a canvas/draw loop ONLY when it matters, to stop
 * phones overheating.
 *
 * It calls your `draw(dt, now)` on each frame, but:
 *   • pauses when the element is scrolled/swiped offscreen (IntersectionObserver)
 *   • pauses when the tab/app is hidden or the phone screen locks (visibilitychange)
 *   • caps the frame rate (default 30fps — ambient scenes look identical, half the cost)
 *
 * Because Swiper keeps neighbouring slides mounted, without this every mounted
 * canvas animates at 60fps at once. With this, only the visible one runs.
 *
 * Usage in a canvas component:
 *
 *   const wrapRef = useRef<HTMLDivElement>(null);
 *   useVisibleAnimation(wrapRef, (dt, now) => {
 *     // ...your existing per-frame draw code, using `now` where you used performance.now()...
 *   }, { fps: 30 });
 *
 *   return <div ref={wrapRef}><canvas ... /></div>;
 *
 * `dt` is seconds since the previous drawn frame (0 on the first frame after a
 * pause). If your draw uses a frame counter, just keep `frame++` inside draw.
 */
export function useVisibleAnimation(
  ref: React.RefObject<HTMLElement | null>,
  draw: (dt: number, now: number) => void,
  opts: { fps?: number } = {}
) {
  const drawRef = useRef(draw);
  drawRef.current = draw;

  useEffect(() => {
    const el = ref.current;
    const minInterval = 1000 / (opts.fps ?? 30);

    let raf = 0;
    let visibleOnScreen = true;   // IntersectionObserver
    let pageVisible = !document.hidden;
    let last = 0;

    const running = () => visibleOnScreen && pageVisible;

    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      if (!running()) { last = 0; return; }
      if (last && now - last < minInterval) return;   // fps cap
      const dt = last ? (now - last) / 1000 : 0;
      last = now;
      drawRef.current(dt, now);
    };

    // pause/resume on scroll offscreen
    let io: IntersectionObserver | null = null;
    if (el && "IntersectionObserver" in window) {
      io = new IntersectionObserver(
        entries => { visibleOnScreen = entries[0]?.isIntersecting ?? true; if (running()) last = 0; },
        { threshold: 0.01 }
      );
      io.observe(el);
    }

    // pause/resume on tab hide / screen lock
    const onVis = () => { pageVisible = !document.hidden; if (running()) last = 0; };
    document.addEventListener("visibilitychange", onVis);

    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      io?.disconnect();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [ref, opts.fps]);
}

export default useVisibleAnimation;
